import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';

const clientsList = [
  { id: 1, name: 'Medibill Connect', image: '/assets/img/clients/medibillconnect.png' },
  { id: 2, name: 'SmartMDMed', image: '/assets/img/clients/smartmdmed.png' },
  { id: 3, name: 'AmeryMed', image: '/assets/img/clients/amerymed.png' },
  { id: 4, name: 'The City Centre Clinic', image: '/assets/img/clients/CityCentreClinic.jpg' },
  { id: 5, name: 'HiTech Home Designs', image: '/assets/img/clients/hitechhomedesigns.png' },
  { id: 6, name: 'Grill Express MC', image: '/assets/img/clients/grillexpressmc.png' },
  { id: 7, name: 'Kids Space Daycare', image: '/assets/img/clients/kidsspacedaycare.png' },
  { id: 8, name: 'Les Petits Lauréats', image: '/assets/img/clients/lespetitslaureats.png' },
  { id: 9, name: 'AmeryTechNet', image: '/assets/img/clients/amerytechnet.png' },
  { id: 10, name: 'VMED Solutions', image: '/assets/img/clients/vmedsolutions.png' },
  { id: 11, name: 'ECRC AZ Club', image: '/assets/img/clients/ecrcaz.ico' },
  { id: 12, name: '4Monsters', image: '/assets/img/clients/fourmonstersLogoMain.jpg' },
  { id: 13, name: 'AHL Pharmacy', image: '/assets/img/clients/AHLLogo.png' },
];

const testimonialsList = [
  {
    id: 1,
    quote: "“Perfect Signs Canada designed our clinic's window graphics and storefront signs. The visibility is incredible, and we've had countless new patients mention they found us just by driving past our new location.”",
    author: "Dr. Sarah Miller",
    company: "Clinical Director, The City Centre Clinic"
  },
  {
    id: 2,
    quote: "“We hired Amit to build our online ordering web app and set up our digital POS systems. The platform handles peak hours flawlessly and has completely streamlined our restaurant payments.”",
    author: "Marcus Thorne",
    company: "Owner, Grill Express MC"
  },
  {
    id: 3,
    quote: "“The custom storefront signage and banners they installed for our daycare are beautiful. We noticed a major jump in local inquiries, and enrollment doubled within a month of putting the sign up!”",
    author: "Amy Chen",
    company: "Founder, Kids Space Daycare"
  },
  {
    id: 4,
    quote: "“Amit designed our corporate website and set up our local SEO configurations. Our search engine rankings soared within three weeks of launch. His design standards and technical execution are top notch.”",
    author: "David Vance",
    company: "Managing Partner, HiTech Home Designs"
  },
  {
    id: 5,
    quote: "“The serverless WebAPI portal they engineered for our medical billing software is highly secure, fast, and handles thousands of claims daily. Perfect Signs has been a key technology partner in our growth.”",
    author: "Jessica Reynolds",
    company: "Operations Manager, Medibill Connect"
  },
  {
    id: 6,
    quote: "“The custom school decals, welcome safety signs, and brand logo they designed for our preschool made a huge impact at our open house. Parents frequently comment on how professional our branding feels.”",
    author: "Sophie Dubois",
    company: "Director, Les Petits Lauréats"
  }
];

// Duplicate the lists to make infinite horizontal marquee loop seamless
const scrollingClients = [...clientsList, ...clientsList];
const scrollingTestimonials = [...testimonialsList, ...testimonialsList];

export default function Home() {
  const homeSchema = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": "Perfect Signs Canada",
    "image": "https://perfectsignscanada.com/assets/img/PerfectSignsLogoFinal.jpg",
    "url": "https://perfectsignscanada.com/",
    "telephone": "+1-780-554-3949",
    "email": "info@perfectsignscanada.com",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "6109 176 Ave NW",
      "addressLocality": "Edmonton",
      "addressRegion": "AB",
      "postalCode": "T5Y 0K8",
      "addressCountry": "CA"
    },
    "areaServed": "Canada",
    "priceRange": "$$",
    "description": "Custom signage, wraps, websites, SEO, marketing, and POS solutions for Canadian businesses.",
    "sameAs": [
      "https://perfectsignscanada.com/"
    ]
  };

  return (
    <>
      <SEO 
        title="Perfect Signs Canada | Custom Signs, Wraps, Websites & Marketing"
        description="Perfect Signs Canada designs custom business signs, banners, vehicle wraps, window graphics, websites, SEO, marketing, and POS solutions for businesses in Edmonton and across Canada."
        keywords="custom signs Edmonton, business signs Canada, portable road signs, window graphics, vehicle wraps, channel letters, banners, pylon signs, website design Edmonton, SEO services, digital marketing, POS systems"
        canonical="https://perfectsignscanada.com/"
        schema={homeSchema}
      />

      {/* ======= Hero Section ======= */}
      <section id="hero" className="hero d-flex align-items-center">
        <div className="container">
          <div className="row">
            <div className="col-xl-8 col-lg-9">
              <p className="hero-eyebrow" data-aos="fade-up">Signage, websites, marketing and POS support</p>
              <h2 data-aos="fade-up">Make your brand impossible to miss.</h2>
              <blockquote data-aos="fade-up" data-aos-delay="100">
                <h5>Perfect Signs Canada helps businesses look professional in the street, online, and at the point of sale.</h5>
                <p>Custom signage | Vehicle wraps | Window graphics | Websites | SEO | Marketing | POS systems</p>
              </blockquote>
              <div className="hero-actions d-flex flex-wrap gap-3" data-aos="fade-up" data-aos-delay="200">
                <Link to="/contact" className="btn-get-started" id="btn-request-quote">Request a Quote</Link>
                <Link to="/signage" className="btn-view-work" id="btn-explore-work">Explore Our Work</Link>
              </div>
              <div className="hero-proof" data-aos="fade-up" data-aos-delay="300">
                <span><strong>Edmonton based</strong> serving Canada</span>
                <span><strong>Design to install</strong> support</span>
                <span><strong>Signage + digital</strong> in one place</span>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* End Hero Section */}

      {/* ======= Home Intro Section ======= */}
      <section className="home-intro">
        <div className="container" data-aos="fade-up">
          <div className="row gy-4 align-items-center">
            <div className="col-lg-5">
              <img src="/assets/img/why-us-bg.jpg" className="img-fluid home-intro-photo" alt="Custom signage designed by Perfect Signs Canada" />
            </div>
            <div className="col-lg-7">
              <div className="home-intro-content ps-lg-4">
                <p className="section-kicker">One partner for visibility</p>
                <h2>We help businesses look polished wherever customers find them.</h2>
                <p>Perfect Signs Canada brings signage, websites, marketing, and practical business technology together so your brand feels consistent from the storefront to the search result.</p>
                <div className="home-proof-grid">
                  <div>
                    <i className="bi bi-shop-window"></i>
                    <strong>Street presence</strong>
                    <span>Signs, windows, wraps, banners, and displays designed to be noticed.</span>
                  </div>
                  <div>
                    <i className="bi bi-laptop"></i>
                    <strong>Digital presence</strong>
                    <span>Websites, SEO, marketing, hosting, and POS systems that support growth.</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* End Home Intro Section */}

      {/* ======= Capabilities Section ======= */}
      <section className="home-capabilities">
        <div className="container" data-aos="fade-up">
          <div className="section-header">
            <h2>What We Do</h2>
            <p>Focused services for businesses that want to be easier to notice, easier to trust, and easier to choose.</p>
          </div>
          <div className="row gy-4">
            <div className="col-lg-4">
              <Link className="capability-card signage-card" to="/signage" id="card-signage">
                <span>Signage</span>
                <h3>Custom signs, wraps, windows, banners, and displays.</h3>
                <p>From portable road signs to vehicle wraps and channel letters, we help your physical brand presence work harder.</p>
                <strong>View signage work <i className="bi bi-arrow-right"></i></strong>
              </Link>
            </div>
            <div className="col-lg-4">
              <Link className="capability-card digital-card" to="/services" id="card-web-it">
                <span>Web & IT</span>
                <h3>Websites, hosting, POS, ordering, and integrations.</h3>
                <p>Clean digital tools for businesses that need practical systems, secure setup, and reliable support.</p>
                <strong>Explore IT services <i class="bi bi-arrow-right"></i></strong>
              </Link>
            </div>
            <div className="col-lg-4">
              <Link className="capability-card marketing-card-home" to="/marketing" id="card-marketing">
                <span>Marketing</span>
                <h3>Social, Google, email, WhatsApp, and campaign support.</h3>
                <p>Marketing that connects your brand, offer, signage, website, and customer follow-up.</p>
                <strong>Plan marketing <i class="bi bi-arrow-right"></i></strong>
              </Link>
            </div>
          </div>
        </div>
      </section>
      {/* End Capabilities Section */}

      {/* ======= Home Featured Section ======= */}
      <section className="home-featured">
        <div className="container" data-aos="fade-up">
          <div className="row gy-4 align-items-stretch">
            <div className="col-lg-5">
              <div className="featured-copy">
                <p className="section-kicker">Featured signage</p>
                <h2>Built for visibility, readability, and real locations.</h2>
                <p>Good signage is not only decoration. It guides people, sells from a distance, makes your storefront easier to remember, and gives your business a more professional first impression.</p>
                <Link to="/signage">Browse portfolio <i class="bi bi-arrow-right"></i></Link>
              </div>
            </div>
            <div className="col-lg-7">
              <div className="featured-grid">
                <div className="featured-item large" style={{ backgroundImage: "url('/assets/img/ChannelLetter.jpg')" }}>
                  <span>Channel Letters</span>
                </div>
                <div className="featured-item" style={{ backgroundImage: "url('/assets/img/VehicleWrap.jpg')" }}>
                  <span>Vehicle Wraps</span>
                </div>
                <div className="featured-item" style={{ backgroundImage: "url('/assets/img/WindowGraphics.jpg')" }}>
                  <span>Window Graphics</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* End Home Featured Section */}

      {/* ======= Home Process Section ======= */}
      <section className="home-process">
        <div className="container" data-aos="fade-up">
          <div className="section-header">
            <h2>How We Work</h2>
          </div>
          <div className="row gy-4">
            <div className="col-lg-3 col-md-6">
              <div className="process-card">
                <span>01</span>
                <h3>Understand</h3>
                <p>We learn your business, location, audience, offer, and timeline.</p>
              </div>
            </div>
            <div className="col-lg-3 col-md-6">
              <div className="process-card">
                <span>02</span>
                <h3>Design</h3>
                <p>We shape clear visuals, messaging, and practical recommendations.</p>
              </div>
            </div>
            <div className="col-lg-3 col-md-6">
              <div className="process-card">
                <span>03</span>
                <h3>Build</h3>
                <p>We produce signage, websites, systems, or campaigns with care.</p>
              </div>
            </div>
            <div className="col-lg-3 col-md-6">
              <div className="process-card">
                <span>04</span>
                <h3>Support</h3>
                <p>We help you launch, improve, and keep the work useful over time.</p>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* End Home Process Section */}

      {/* ======= Clients Section (Trusted By Businesses Globally) Horizontal Marquee ======= */}
      <section id="clients" className="home-clients">
        <div className="container" data-aos="fade-up">
          <div className="section-header">
            <h2>Trusted By Businesses Globally</h2>
          </div>
          <div className="clients-scroller">
            <div className="clients-track">
              {scrollingClients.map((client, idx) => (
                <div key={idx} className="client-logo-badge">
                  <img src={client.image} alt={client.name} />
                  <span>{client.name}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
      {/* End Clients Section */}

      {/* ======= Testimonials Section (Realistic Quotes Linked to Real Clients) ======= */}
      <section className="home-testimonials">
        <div className="container" data-aos="fade-up">
          <div className="section-header">
            <h2>What Clients Say</h2>
          </div>
          <div className="testimonials-scroller">
            <div className="testimonials-track">
              {scrollingTestimonials.map((t, idx) => (
                <div key={idx} className="home-quote">
                  <div className="stars">
                    <i className="bi bi-star-fill"></i>
                    <i className="bi bi-star-fill"></i>
                    <i className="bi bi-star-fill"></i>
                    <i className="bi bi-star-fill"></i>
                    <i className="bi bi-star-fill"></i>
                  </div>
                  <p>{t.quote}</p>
                  <strong>{t.author}</strong>
                  <span className="quote-company d-block text-muted mt-1" style={{ fontSize: '13px' }}>{t.company}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
      {/* End Testimonials Section */}

      {/* ======= Home CTA Section ======= */}
      <section className="home-cta">
        <div className="container" data-aos="fade-up">
          <div className="row align-items-center gy-4">
            <div className="col-lg-8">
              <p className="section-kicker">Ready to improve your visibility?</p>
              <h2>Tell us what you want customers to notice first.</h2>
              <p>We can help with a single sign, a complete storefront refresh, a better website, or a campaign that brings everything together.</p>
            </div>
            <div className="col-lg-4 text-lg-end">
              <Link className="home-cta-btn" to="/contact">Request a Quote</Link>
            </div>
          </div>
        </div>
      </section>
      {/* End Home CTA Section */}
    </>
  );
}
